import { type SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import { type MentionAnalyticsEvent } from '../../../common/services/rovo-chat-mention-resource/mention-analytics-event';
import { type AgentMentionConversationContext } from '../types';

import { buildTurnContext } from './build-turn-context';

/**
 * Instruments the `@mention` picker funnel: menu open/search
 * (`mentionAgentMenu`), result selection (`mentionResult`), and agent switch
 * (`mentionAgent`).
 */
export const sendMentionMenuAnalyticsEvent = ({
	event,
	previousAgentId,
	previousAgentName,
	previousAgentCreatorType,
	conversationAgentId,
	chatSessionId,
	chatTurnNumber,
	sendAnalyticsEvent,
}: AgentMentionConversationContext & {
	event: MentionAnalyticsEvent;
	previousAgentId: string | undefined;
	previousAgentName?: string | undefined;
	previousAgentCreatorType?: string | undefined;
	sendAnalyticsEvent: SendAnalyticsEvent;
}): void => {
	const conversationContext = conversationAgentId ? { conversationAgentId } : {};
	const resultCountContext = {
		agentResultCount: event.agentResultCount,
		peopleResultCount: event.peopleResultCount,
		teamResultCount: event.teamResultCount,
	};
	const turnContext = buildTurnContext(chatSessionId, chatTurnNumber);

	if (event.action === 'selected') {
		if (event.resultType === 'person' || event.resultType === 'team') {
			sendAnalyticsEvent({
				action: 'selected',
				actionSubject: 'mentionResult',
				eventType: 'ui',
				attributes: {
					resultType: event.resultType,
					position: event.position,
					queryLength: event.queryLength,
					...resultCountContext,
					...turnContext,
					...conversationContext,
				},
			});
			return;
		}

		// Names of private (customer-authored) agents may carry sensitive text, so
		// only include the name for non-private agents. An unknown creator type is
		// treated as private (name omitted) to stay on the safe side.
		const includeAgentName =
			event.agentCreatorType !== undefined && event.agentCreatorType !== 'CUSTOMER';

		sendAnalyticsEvent({
			action: 'selected',
			actionSubject: 'mentionResult',
			eventType: 'ui',
			attributes: {
				resultType: event.resultType,
				agentId: event.agentId,
				agentCreatorType: event.agentCreatorType,
				agentIsVerified: event.agentIsVerified,
				position: event.position,
				queryLength: event.queryLength,
				...resultCountContext,
				...turnContext,
				...(includeAgentName && event.agentName ? { agentName: event.agentName } : {}),
				...conversationContext,
			},
		});
		if (
			event.resultType === 'agent' &&
			event.agentId !== undefined &&
			previousAgentId !== undefined &&
			previousAgentId !== event.agentId
		) {
			// Same privacy gate as the new agent's name, applied to the agent
			// being switched away from.
			const includePreviousAgentName =
				previousAgentCreatorType !== undefined && previousAgentCreatorType !== 'CUSTOMER';
			sendAnalyticsEvent({
				action: 'switched',
				actionSubject: 'mentionAgent',
				eventType: 'ui',
				attributes: {
					previousAgentId,
					previousAgentCreatorType,
					...(includePreviousAgentName && previousAgentName ? { previousAgentName } : {}),
					newAgentId: event.agentId,
					newAgentCreatorType: event.agentCreatorType,
					...resultCountContext,
					...turnContext,
					...(includeAgentName && event.agentName ? { newAgentName: event.agentName } : {}),
					...conversationContext,
				},
			});
		}
		return;
	}

	sendAnalyticsEvent({
		action: event.action,
		actionSubject: 'mentionAgentMenu',
		eventType: 'ui',
		attributes: {
			queryLength: event.queryLength,
			agentResultCount: event.agentResultCount,
			peopleResultCount: event.peopleResultCount,
			teamResultCount: event.teamResultCount,
			...turnContext,
			...conversationContext,
		},
	});
};
